import styled from "@emotion/styled";
import { useMemo } from "react";
import { Outlet, useMatches, type To } from "react-router";

import { useFeatureSwitch } from "@skilitics/designer-auth/state";
import {
  LinkTab,
  PAGE_SIDE_PADDING,
  PageContainer,
  PageContentContainer,
  PageHeader,
  PageHeaderTabsContainer,
  PageHeaderTitle,
  Tabs,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { useWorkspaceInfo } from "../../../auth/workspaces.ts";
import type { SettingsRouteId } from "./settingsRoute.ts";

export const SettingsPage = () => {
  return (
    <PageContainer>
      <SettingsPageHeader />
      <ContentContainer>
        <Outlet />
      </ContentContainer>
    </PageContainer>
  );
};

const TabRow = styled.nav`
  display: flex;
  flex-flow: row;
`;

const TabGroup = styled.div`
  display: flex;
  flex-flow: column;
`;

const TabGroupName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.generalTextIcon};
  margin: 0 16px;
`;

const TabSeparator = styled.div`
  width: 1px;
  background-color: ${({ theme }) => theme.outlineSoft};
  margin: 0 8px 16px;
`;

const StyledTab = styled(LinkTab)``;

interface RouteTabItem {
  // the id of the route this tab selects on; also drives `data-test`
  id: SettingsRouteId;
  // optional for disabled tabs we have not yet implemented
  to?: To;
  label: string;
}

// Super annoyingly, MUI Tabs doesn't let you separate the tab selection from layout or styling.
// In particular, `<Tabs>` must directly contain `<Tab>` children, and the current tab selection
// must exist in the <Tabs> children.
const RouteTabList = ({ routes }: { routes: RouteTabItem[] }) => {
  const matches = useMatches();

  const value = useMemo((): string | false => {
    // Finding the match with the longest pathname in `routes` breaks with the
    // current routes, because /settings matches both /settings and /settings/billing,
    // Instead, use the explicit match/route id. This also means we don't have search matches in
    // reverse order since only one match will have the same id.
    for (const match of matches) {
      for (const child of routes) {
        if (match.id === child.id) {
          return child.id;
        }
      }
    }
    // Must explicitly return false otherwise MUI Tabs will yell at you for .
    return false;
  }, [matches, routes]);

  return (
    <Tabs value={value}>
      {routes.map(({ id, to, label }) => (
        <StyledTab key={id} value={id} to={to ?? ""} disabled={!to} label={label} data-test={`${id}-tab`} />
      ))}
    </Tabs>
  );
};

// Saves an extra div to override the padding here. That both adds the
// gap between the tabs and <PageSection>s, and ensures the DangerZoneSection
// outline doesn't get clipped by the default overflow: hidden.
const ContentContainer = styled(PageContentContainer)`
  display: flex;
  flex-flow: column;
  gap: 20px;
  padding: 20px ${PAGE_SIDE_PADDING}px;
`;

const SettingsPageHeader = () => {
  const formatMessage = useFormatMessage(formatStrings);
  const hasWorkspaces = useFeatureSwitch("org-admin");

  const workspace = useWorkspaceInfo();

  const workspaceTabs: (RouteTabItem | false)[] = [
    {
      id: "settings-workspace-general",
      to: "/settings/workspace",
      label: formatMessage("generalSettings"),
    },
    hasWorkspaces &&
      workspace?.role === "admin" && {
        id: "settings-workspace-members",
        to: "/settings/workspace/members",
        label: formatMessage("membersSettings"),
      },
    workspace?.role === "admin" && {
      id: "settings-billing",
      to: "/settings/billing",
      label: formatMessage("billingSettings"),
    },
    workspace?.role === "admin" && {
      id: "settings-integrations",
      to: "/settings/integrations",
      label: formatMessage("integrationsSettings"),
    },
  ];

  return (
    <PageHeader>
      <PageHeaderTitle title={formatMessage("heading")} subTitle={formatMessage("subTitle")} />
      <PageHeaderTabsContainer>
        <TabRow data-test="tabs-container">
          <TabGroup>
            <TabGroupName>{formatMessage("personalGroup")}</TabGroupName>
            <RouteTabList
              routes={[{ id: "settings-profile", to: "/settings", label: formatMessage("profileSettings") }]}
            />
          </TabGroup>
          <TabSeparator />
          <TabGroup>
            <TabGroupName>{workspace?.name}</TabGroupName>
            <RouteTabList routes={workspaceTabs.filter((tab) => !!tab)} />
          </TabGroup>
        </TabRow>
      </PageHeaderTabsContainer>
    </PageHeader>
  );
};

const formatStrings = {
  heading: {
    en: "Administration",
  },
  subTitle: {
    en: "Manage your profile, billing and preferences",
  },
  personalGroup: {
    en: "Personal",
  },
  workspaceGroup: {
    en: "{name} Workspace",
  },
  profileSettings: {
    en: "My Profile",
  },
  generalSettings: {
    en: "General",
  },
  membersSettings: {
    en: "Members",
  },
  billingSettings: {
    en: "Billing",
  },
  integrationsSettings: {
    en: "AI Integration",
  },
};
